import { trpc } from 'src/trpc';
import { CustomerDetailsForm } from './CustomerDetailsForm';
import { useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Loader } from 'components/Loader';
import { AddressForm } from 'components/address/AddressForm';
import { useEffect, useState } from 'react';
import { OrderDashboard } from 'components/orders/OrderDashboard';
import { InvoiceDashboard } from 'components/invoices/InvoiceDashboard';
import { CreateInvoiceSheet } from 'src/modals/CreateInvoiceSheet';
import { Button } from '@/components/ui/button';
import { UserCheck } from 'lucide-react';
import { useToast } from 'components/useToast';
export const CustomerDetailsPage = () => {
  const params = useParams();
  const [addressAccordionIsOpen, setAddressAccordionIsOpen] = useState(false);
  const [orderAccordionIsOpen, setOrderAccordionIsOpen] = useState(false);
  const [invoiceAccordionIsOpen, setInvoiceAccordionIsOpen] = useState(false);
  const [cachedCustomer, setCachedCustomer] = useState<{ userId: string }>({ userId: '' });
  const { successToast, errorToast } = useToast();

  const { data: customer, refetch: refetchCustomer } =
    trpc.admin.customer.adminGetCustomer.useQuery(params.id!);

  const { data: addresses, isLoading: addressesLoading } =
    trpc.admin.address.adminListUserAddresses.useQuery(cachedCustomer.userId, {
      enabled: !!cachedCustomer.userId && addressAccordionIsOpen,
    });

  useEffect(() => {
    if (customer) {
      setCachedCustomer({ userId: customer.userId });
    }
  }, [customer, setCachedCustomer]);

  const handleCustomerUpdateSuccess = () => {
    successToast({
      title: 'Customer Updated',
      description: 'Customer Successfully Updated',
    });
    refetchCustomer();
  };

  const { mutate: masqueradeAsUser, isPending: isMasquerading } =
    trpc.admin.user.masqueradeAsUser.useMutation({
      onSuccess: (data) => {
        successToast({
          title: 'Login as customer authentication successful',
          description: 'Opening customer application in new tab...',
        });
        // Open the customer application with the masquerade token
        // customerAppUrl comes from the backend (reads APP_URL env var)
        const masqueradeUrl = `${data.customerAppUrl}?adminToken=${data.customToken}`;
        window.open(masqueradeUrl, '_blank');
      },
      onError: () => {
        errorToast({
          title: 'Login as Customer Failed',
          description: 'Failed to generate token',
        });
      },
    });

  const handleMasquerade = () => {
    if (customer?.userId) {
      masqueradeAsUser({ userId: customer.userId });
    }
  };

  return (
    <div className="w-full h-full flex flex-col p-[24px] gap-6 min-h-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Customer Details</h1>
        {customer && customer.roles?.includes('customer') && (
          <Button
            onClick={handleMasquerade}
            disabled={isMasquerading || !customer.userId}
            variant="default"
            className="flex items-center gap-2"
          >
            <UserCheck className="h-4 w-4" />
            {isMasquerading ? 'Generating Token...' : 'Login as User'}
          </Button>
        )}
      </div>
      <div className="card w-[833px]">
        <CustomerDetailsForm onSuccess={handleCustomerUpdateSuccess} />
      </div>
      <div className="card w-[833px]">
        <Accordion
          className="w-full"
          type="single"
          collapsible
          data-state={addressAccordionIsOpen ? 'open' : 'close'}
        >
          <AccordionItem value="item-1" className="w-full">
            <AccordionTrigger onClick={() => setAddressAccordionIsOpen(!addressAccordionIsOpen)}>
              <p className="text-xl font-semibold">Addresses</p>
            </AccordionTrigger>
            <AccordionContent className="w-full">
              {addressesLoading && <Loader />}
              {addresses &&
                addresses?.map((address) => {
                  return (
                    <div className="card bg-slate-50 p-4 mb-4">
                      <AddressForm
                        existingAddress={address}
                        key={address._id}
                        addressType={address.type}
                        googlePlacesId={address.googlePlacesId}
                        userId={cachedCustomer.userId}
                      />
                    </div>
                  );
                })}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
      <div className="card w-[833px]">
        <Accordion
          className="w-full"
          type="single"
          collapsible
          data-state={orderAccordionIsOpen ? 'open' : 'close'}
        >
          <AccordionItem value="item-2" className="w-full">
            <AccordionTrigger onClick={() => setOrderAccordionIsOpen(!orderAccordionIsOpen)}>
              <p className="text-xl font-semibold">Orders</p>
            </AccordionTrigger>
            <AccordionContent className="w-full">
              {cachedCustomer.userId && <OrderDashboard userId={cachedCustomer.userId} />}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
      <div className="card w-[833px]">
        <Accordion
          className="w-full"
          type="single"
          collapsible
          data-state={invoiceAccordionIsOpen ? 'open' : 'close'}
        >
          <AccordionItem value="item-3" className="w-full">
            <AccordionTrigger onClick={() => setInvoiceAccordionIsOpen(!invoiceAccordionIsOpen)}>
              <div className="flex justify-between items-center w-full pr-4">
                <p className="text-xl font-semibold">Invoices</p>
                {cachedCustomer.userId && (
                  <CreateInvoiceSheet addButtonVariant="outline" userId={cachedCustomer.userId} />
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="w-full">
              {cachedCustomer.userId && <InvoiceDashboard />}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};
